import * as React from 'react'
import clsx from 'clsx'
import { SxProps } from '../sx'
import { IconButton, IconButtonProps } from '../IconButton'
import { SvgIconProps } from '../SvgIcon'
import { Paper, PaperProps } from '../Paper'
import SemanticSuccess from '../internal/svg-icons/SemanticSuccess'
import SemanticWarning from '../internal/svg-icons/SemanticWarning'
import SemanticError from '../internal/svg-icons/SemanticError'
import SemanticInformation from '../internal/svg-icons/SemanticInformation'
import InfoCircle from '../internal/svg-icons/InfoCircle'
import CloseIcon from '../internal/svg-icons/Close'
import styles from './index.module.scss'
import { capitalize } from '../../utils'

export interface AlertClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `variant="filled"`. */
  filled: string
  /** Styles applied to the root element if `variant="outlined"`. */
  outlined: string
  /** Styles applied to the root element if `variant="standard"`. */
  standard: string
  /** Styles applied to the root element if `variant="standard"` and `color="primary"`. */
  standardPrimary: string
  /** Styles applied to the root element if `variant="standard"` and `color="secondary"`. */
  standardSecondary: string
  /** Styles applied to the root element if `variant="standard"` and `color="success"`. */
  standardSuccess: string
  /** Styles applied to the root element if `variant="standard"` and `color="info"`. */
  standardInfo: string
  /** Styles applied to the root element if `variant="standard"` and `color="warning"`. */
  standardWarning: string
  /** Styles applied to the root element if `variant="standard"` and `color="error"`. */
  standardError: string
  /** Styles applied to the root element if `variant="outlined"` and `color="primary"`. */
  outlinedPrimary: string
  /** Styles applied to the root element if `variant="outlined"` and `color="secondary"`. */
  outlinedSecondary: string
  /** Styles applied to the root element if `variant="outlined"` and `color="success"`. */
  outlinedSuccess: string
  /** Styles applied to the root element if `variant="outlined"` and `color="info"`. */
  outlinedInfo: string
  /** Styles applied to the root element if `variant="outlined"` and `color="warning"`. */
  outlinedWarning: string
  /** Styles applied to the root element if `variant="outlined"` and `color="error"`. */
  outlinedError: string
  /** Styles applied to the root element if `variant="filled"` and `color="primary"`. */
  filledPrimary: string
  /** Styles applied to the root element if `variant="filled"` and `color="secondary"`. */
  filledSecondary: string
  /** Styles applied to the root element if `variant="filled"` and `color="success"`. */
  filledSuccess: string
  /** Styles applied to the root element if `variant="filled"` and `color="info"`. */
  filledInfo: string
  /** Styles applied to the root element if `variant="filled"` and `color="warning"`. */
  filledWarning: string
  /** Styles applied to the root element if `variant="filled"` and `color="error"`. */
  filledError: string
  /** Styles applied to the icon wrapper element. */
  icon: string
  /** Styles applied to the message wrapper element. */
  message: string
  /** Styles applied to the action wrapper element if `action` is provided. */
  action: string
}

export type AlertColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'error'

export interface AlertProps extends Omit<PaperProps, 'variant'> {
  /**
   * The action to display. It renders after the message, at the end of the alert.
   */
  action?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<AlertClasses>
  /**
   * Override the default label for the *close popup* icon button.
   *
   * For localization purposes, you can use the provided [translations](/material-ui/guides/localization/).
   * @default 'Close'
   */
  closeText?: string
  /**
   * The severity of the alert. This defines the color and icon used.
   * @default 'success'
   */
  severity?: AlertColor
  /**
   * Override the icon displayed before the children.
   * Unless provided, the icon is mapped to the value of the `severity` prop.
   * Set to `false` to remove the `icon`.
   */
  icon?: React.ReactNode
  /**
   * The ARIA role attribute of the element.
   * @default 'alert'
   */
  role?: string
  /**
   * The component maps the `severity` prop to a range of different icons,
   * for instance success to `<SuccessOutlined>`.
   * If you wish to change this mapping, you can provide your own.
   * Alternatively, you can use the `icon` prop to override the icon displayed.
   */
  iconMapping?: Partial<Record<AlertColor, React.ReactNode>>
  /**
   * Callback fired when the component requests to be closed.
   * When provided and no `action` prop is set, a close icon button is displayed that triggers the callback when clicked.
   * @param {React.SyntheticEvent} event The event source of the callback.
   */
  onClose?: (event: React.SyntheticEvent) => void
  /**
   * The variant to use.
   * @default 'standard'
   */
  variant?: 'standard' | 'filled' | 'outlined'
  /**
   * The extra props for the slot components.
   * You can override the existing props or add new ones.
   *
   * This prop is an alias for the `componentsProps` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slotProps?: {
    closeButton?: IconButtonProps
    closeIcon?: SvgIconProps
  }
  /**
   * The components used for each slot inside.
   *
   * This prop is an alias for the `components` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slots?: {
    closeButton?: React.ElementType
    closeIcon?: React.ElementType
  }
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

const defaultIconMapping = {
  success: <SemanticSuccess fontSize="inherit" />,
  warning: <SemanticWarning fontSize="inherit" />,
  error: <SemanticError fontSize="inherit" />,
  info: <SemanticInformation fontSize="inherit" />,
  primary: <InfoCircle fontSize="inherit" />,
  secondary: <InfoCircle fontSize="inherit" />,
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    const {
      action,
      children,
      className,
      closeText = 'Close',
      icon,
      iconMapping = defaultIconMapping,
      onClose,
      role = 'alert',
      severity = 'success',
      slotProps = {},
      slots = {},
      variant = 'standard',
      classes,
      ...other
    } = props

    const AlertCloseButton = slots.closeButton ?? IconButton
    const AlertCloseIcon = slots.closeIcon ?? CloseIcon

    const closeButtonProps = slotProps.closeButton
    const closeIconProps = slotProps.closeIcon

    return (
      <Paper
        role={role}
        elevation={0}
        className={clsx(
          [styles.root, classes?.root],
          [styles[`variant-${variant}`], classes?.[variant]],
          [
            styles[`sevirity-${severity}`],
            // @ts-ignore
            classes?.[`${variant}${capitalize(severity)}`],
          ],
          className
        )}
        ref={ref}
        {...other}
      >
        {icon !== false ? (
          <div className={clsx(styles.alertIcon, classes?.icon)}>
            {icon || iconMapping[severity] || defaultIconMapping[severity]}
          </div>
        ) : null}
        <div className={clsx(styles.alertMessage, classes?.message)}>
          {children}
        </div>
        {action != null ? (
          <div className={clsx(styles.alertAction, classes?.action)}>
            {action}
          </div>
        ) : null}
        {action == null && onClose ? (
          <div className={clsx(styles.alertAction, classes?.action)}>
            <AlertCloseButton
              aria-label={closeText}
              title={closeText}
              color="secondary"
              onClick={onClose}
              {...closeButtonProps}
              classes={{
                ...closeButtonProps?.classes,
                root: clsx(styles.buttonClose, closeButtonProps?.classes?.root),
              }}
              size="small"
            >
              <AlertCloseIcon {...closeIconProps} />
            </AlertCloseButton>
          </div>
        ) : null}
      </Paper>
    )
  }
)
